import React, { useState, useEffect } from 'react';
import { X, Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { Alert, AlertDescription } from '@/components/ui/alert';

export default function NewAnalysisTypeModal({
    isModalOpen,
    setIsModalOpen,
    fetchAnalysisTypes,
    fetchUserCredits
}) {
    const { user } = useAuth();
    const [isCreatingType, setIsCreatingType] = useState(false);
    const [error, setError] = useState('');
    const [creditsCost, setCreditsCost] = useState(10);
    const [newAnalysisType, setNewAnalysisType] = useState({
        name: '',
        description: '',
        includes: ''
    });

    // Fetch credit cost from settings
    useEffect(() => {
        async function fetchCreditCost() {
            try {
                const { data, error } = await supabase
                    .from('settings')
                    .select('value')
                    .eq('key', 'custom_type_credits_cost')
                    .single();

                if (error) throw error;
                setCreditsCost(data.value);
            } catch (err) {
                console.error('Error fetching credit cost:', err);
                // Fallback to default value if there's an error
            }
        }

        fetchCreditCost();
    }, []);

    const handleCreateAnalysisType = async (e) => {
        e.preventDefault();
        setIsCreatingType(true);
        setError('');

        try {
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) {
                throw new Error('Please sign in to continue');
            }

            // Check if user has enough credits
            const { data: profile, error: profileError } = await supabase
                .from('profiles')
                .select('credits')
                .eq('id', user.id)
                .single();

            if (profileError) throw new Error('Could not verify credits');
            if (profile.credits < creditsCost) {
                throw new Error(`Insufficient credits. You need ${creditsCost} credits to create a custom analysis type.`);
            }

            // Create the analysis type through the backend
            const response = await fetch(`${import.meta.env.VITE_API_URL}/create-analysis-type`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`
                },
                body: JSON.stringify({
                    ...newAnalysisType,
                    user_id: user.id
                })
            });

            if (!response.ok) {
                const errorBody = await response.json();
                throw new Error(errorBody.error || 'Failed to create analysis type');
            }

            const data = await response.json();

            // After successful creation
            setNewAnalysisType({
                name: '',
                description: '',
                includes: ''
            });
            setIsModalOpen(false);

            // Refresh both analysis types and credits
            await Promise.all([
                fetchAnalysisTypes(),
                fetchUserCredits()  // Add this line
            ]);
        } catch (err) {
            setError(err.message);
            console.error('Error creating analysis type:', err);
        } finally {
            setIsCreatingType(false);
        }
    };

    if (!isModalOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50" style={{ margin: 0 }}>
            <div className="relative bg-white dark:bg-gray-800 rounded-lg p-8 m-4 max-w-xl w-full max-h-[90vh] overflow-y-auto">
                <button
                    onClick={() => setIsModalOpen(false)}
                    className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                    disabled={isCreatingType}
                >
                    <X className="h-6 w-6" />
                </button>

                <h2 className="text-2xl font-bold mb-6 dark:text-white">Create New Analysis Type</h2>

                <Alert className="mb-6 bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800">
                    <div className="flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                        <AlertDescription className="text-blue-700 dark:text-blue-300 text-sm">
                            Creating a custom analysis type costs {creditsCost} credits
                        </AlertDescription>
                    </div>
                </Alert>

                <form onSubmit={handleCreateAnalysisType} className="space-y-6">
                    {/* Name Input */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Analysis Type Name <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="text"
                            value={newAnalysisType.name}
                            onChange={(e) => setNewAnalysisType(prev => ({ ...prev, name: e.target.value }))}
                            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            placeholder="e.g., Electronics, Books, Fashion"
                            required
                            minLength={3}
                            disabled={isCreatingType}
                        />
                    </div>

                    {/* Description */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Description <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            value={newAnalysisType.description}
                            onChange={(e) => setNewAnalysisType(prev => ({ ...prev, description: e.target.value }))}
                            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md h-24 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            placeholder="Describe what kind of websites should be analyzed..."
                            required
                            disabled={isCreatingType}
                        />
                    </div>

                    {/* What to Include */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            What types of products or services should be included? <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            value={newAnalysisType.includes}
                            onChange={(e) => setNewAnalysisType(prev => ({ ...prev, includes: e.target.value }))}
                            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md h-24 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                            placeholder="List types of products/services that should be marked as 'Yes'..."
                            required
                            disabled={isCreatingType}
                        />
                    </div>

                    {error && (
                        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 p-3 rounded-md">
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={isCreatingType}
                        className={`w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white flex items-center justify-center
                            ${isCreatingType
                                ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600'
                            }`}
                    >
                        {isCreatingType ? (
                            <>
                                <Loader2 className="animate-spin h-4 w-4 mr-2" />
                                Creating...
                            </>
                        ) : (
                            'Create Analysis Type'
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
}